import { useState } from 'react';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { Button, Input } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { api } from '@/utils/api';

/** Above this many teammates the list gets a search field — below it, scanning is faster than typing. */
const SEARCH_THRESHOLD = 6;

interface Props {
  teamId: string;
  /** The current owner, ticked in the list. */
  ownerUserId: string;
  /** Reassign. Called with the picked user id; the caller closes the popover + persists. */
  onPick: (userId: string) => void;
}

/**
 * The teammate list behind {@link SequenceOwnerNode} — who the sequence runs as. Mounted only while the
 * popover is open, so the roster is fetched on first open rather than on every canvas render.
 *
 * Teammates without sequences enabled are shown but NOT selectable: the due-work sweep only picks up arms
 * whose owner has the flag, so handing them the sequence would stall every enrollment with no visible
 * error. They're listed rather than hidden so "why isn't Dave here?" has an answer on the spot.
 */
export function SequenceOwnerPicker({ teamId, ownerUserId, onPick }: Props) {
  const optionsQuery = api.sequence.ownerOptions.useQuery({ teamId });
  const [query, setQuery] = useState('');
  const options = optionsQuery.data ?? [];
  const needle = query.trim().toLowerCase();
  const matches = needle === '' ? options : options.filter((o) => o.label.toLowerCase().includes(needle));

  return (
    <div className="flex w-64 flex-col gap-1">
      {options.length > SEARCH_THRESHOLD && (
        <Input
          autoFocus
          size="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search teammates"
        />
      )}
      <div className="flex max-h-64 flex-col overflow-y-auto">
        {optionsQuery.isLoading && (
          <span className="flex items-center gap-2 px-2 py-3 text-xs text-gray-500 dark:text-gray-400">
            <WaveSpinner className="size-4" />
            Loading teammates…
          </span>
        )}
        {/* A failed fetch must NOT read as "your team is empty" — that would send the rep looking for the
            wrong problem. Distinct copy, and distinct again from a search that simply matched nothing. */}
        {optionsQuery.isError && (
          <span className="px-2 py-3 text-xs text-red-500">Couldn’t load your teammates — try again.</span>
        )}
        {!optionsQuery.isLoading && !optionsQuery.isError && matches.length === 0 && (
          <span className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400">
            {needle === '' ? 'No one on this team can own a sequence yet.' : 'No teammates match that.'}
          </span>
        )}
        {matches.map((option) => (
          <Button
            key={option.userId}
            text
            size="sm"
            textAlign="left"
            // `readOnly`, NOT `disabled`: a disabled <button> dispatches no mouse events, so the tooltip
            // explaining WHY they're greyed out — the whole reason they're listed — would never show.
            readOnly={!option.canRunSequences}
            className={option.canRunSequences ? undefined : 'opacity-60'}
            tooltip={
              option.canRunSequences
                ? undefined
                : `${option.name ?? 'They'} doesn’t have sequences enabled, so nothing would be sent on their behalf.`
            }
            tooltipMaxWidth={240}
            onClick={option.canRunSequences ? () => onPick(option.userId) : undefined}
            icon={
              <TeamUserAvatar
                userTeam={{ name: option.name, profilePicture: option.profilePicture }}
                shape="circle"
                className="size-full text-2xs"
              />
            }
            rIcon={option.userId === ownerUserId ? <General.Check className="text-blue-500" /> : undefined}>
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
